/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  Input,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { SafePipe } from '../../utils/pipes/safe.pipe';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/app.state';
import { PersonaFetch } from '@workspaces/store/persona.feature';
import { UserManagementFetch } from '@workspaces/store/user-management.feature';
import { WorkspacePersonaFetch } from '@workspaces/store/workspace-persona.feature';
import { Subscription } from 'rxjs';
import { CollaboraMention, CollabraExportFormats, CollabraService } from '@workspaces/utils/collabra.service';

type CollaboraMessage =
  | {
      MessageId: 'App_LoadingStatus';
      Values?: { Status: string };
    }
  | {
      MessageId: 'UI_Mention';
      Values: { type: 'autocomplete' | 'selected'; text: string };
    }
  | { MessageId: 'Get_Export_Formats_Resp'; Values: CollabraExportFormats[] };

@Component({
  selector: 'app-editor-iframe',
  templateUrl: './editor-iframe.component.html',
  styleUrls: ['./editor-iframe.component.scss'],
  imports: [SafePipe, MatButtonModule, MatIcon, CommonModule],
  standalone: true
})
export class EditorIframeComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() collaboraIframeUrl = '';
  @Input({ required: true }) workspaceId = '';

  private mentionList: CollaboraMention[] = [];
  @ViewChild('collaboraIframe') collaboraIframe!: ElementRef<HTMLIFrameElement>;
  @Output() collaboraEmit = new EventEmitter<CollaboraMessage>();
  private readonly sub = new Subscription();

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private store: Store<AppState>,
    public readonly collabraService: CollabraService
  ) {}

  public ngOnInit(): void {
    this.collabraService.setCollabraPostMessageFunction((MessageId: string, Values: any) =>
      this.sendCollaboraPostMessage(MessageId, Values)
    );
    this.fetchInitialRequiredData();
    this.sub.add(
      this.collabraService.getPossibleMentionList(this.workspaceId).subscribe(mentionList => {
        this.mentionList = mentionList;
      })
    );
  }

  public fetchInitialRequiredData() {
    this.store.dispatch(PersonaFetch({ includeHidden: true }));

    //Just to refresh if there was a possible new BE chagne
    if (this.workspaceId) {
      this.store.dispatch(WorkspacePersonaFetch({ workspaceId: this.workspaceId }));
      this.store.dispatch(UserManagementFetch({ workspaceId: this.workspaceId }));
    }
  }

  ngAfterViewInit() {
    window.addEventListener('message', event => {
      this.getCollaboraMessage(event);
    });
  }

  private getCollaboraMessage(event: MessageEvent<any>) {
    let messageData: CollaboraMessage;

    if (typeof event.data === 'string') {
      try {
        messageData = JSON.parse(event.data);
      } catch {
        return;
      }
    } else {
      messageData = event.data;
    }
    if (!messageData || !messageData.MessageId) {
      return;
    }
    console.log('Message received: ', messageData);

    if (
      !this.collabraService.isCollabraIframeInitialized &&
      messageData.MessageId === 'App_LoadingStatus' &&
      messageData.Values?.Status === 'Initialized'
    ) {
      this.sendHostReadyCommand();
      const fetchExports = () =>
        setTimeout(() => {
          if (this.collabraService.exportFormatsInitialized) {
            return;
          }
          this.getExportFormats();
          fetchExports();
        }, 300); // Need some time because it is not initialized on app init
      fetchExports();
    }
    if (messageData.MessageId === 'UI_Mention') {
      this.receiveMention(messageData.Values);
    }
    if (messageData.MessageId === 'Get_Export_Formats_Resp') {
      this.collabraService.setExportFormats(messageData.Values);
    }
    this.collaboraEmit.emit(messageData);
  }

  private getExportFormats() {
    this.sendCollaboraPostMessage('Get_Export_Formats', '');
  }

  private sendHostReadyCommand() {
    const iframe = this.collaboraIframe.nativeElement;

    if (!iframe) {
      return;
    }
    if (iframe?.contentWindow) {
      const message = { MessageId: 'Host_PostmessageReady', SendTime: Date.now() };
      iframe.contentWindow.postMessage(JSON.stringify(message), '*');
      this.collabraService.isCollabraIframeInitialized = true;
    }
  }

  public sendCollaboraPostMessage(MessageId: string, Values: any) {
    const iframe = this.collaboraIframe.nativeElement;
    if (iframe?.contentWindow) {
      const message: Record<string, any> = {
        MessageId,
        SendTime: Date.now()
      };

      message['Values'] = Values;
      iframe.contentWindow.postMessage(message, '*');
    }
  }

  public receiveMention(value: { type: 'autocomplete' | 'selected'; text: string }) {
    if (value.type === 'autocomplete') {
      this.postMention(value.text);
    }
  }

  public postMention(mentionStarter: string) {
    const mentionList: CollaboraMention[] = [
      ...this.mentionList.filter(mentioned =>
        mentioned.label?.toLowerCase().startsWith(mentionStarter.trim().toLowerCase())
      )
    ];
    this.sendCollaboraPostMessage('Action_Mention', { list: mentionList });
  }

  public onScrollToTop(): void {
    const domElementRealWindow = this.document.getElementById('docs-content-holder');

    domElementRealWindow?.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  ngOnDestroy(): void {
    this.collabraService.isCollabraIframeInitialized = false;
    this.sub.unsubscribe();
  }
}
